import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Search, Loader2, ExternalLink, Calendar } from "lucide-react";

const typeMeta = {
  "Sources Sought":   { tile: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "SOURCES SOUGHT" },
  "Presolicitation":  { tile: "bg-sky-100 text-sky-700 border-sky-200",             label: "PRESOL" },
  "Special Notice":   { tile: "bg-violet-100 text-violet-700 border-violet-200",    label: "SPECIAL NOTICE" },
  "Justification":    { tile: "bg-amber-100 text-amber-700 border-amber-200",       label: "J&A" },
  "Intent to Bundle": { tile: "bg-rose-100 text-rose-700 border-rose-200",          label: "INTENT" },
};

export default function Forecaster() {
  const [f, setF] = useState({ naics: "", state: "", keyword: "", days_back: 30 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null) p.append(k, v); });
      const r = await api.get(`/forecaster/notices?${p.toString()}`);
      setRows(r.data || []);
      if (!firstLoad) toast.success(`${(r.data || []).length} pre-solicitation notices`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forecaster query failed");
    } finally { setLoading(false); setFirstLoad(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = rows.reduce((acc, r) => { const t = r.type || "Other"; acc[t] = (acc[t] || 0) + 1; return acc; }, {});

  return (
    <div className="p-8 stagger-in" data-testid="forecaster-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="bf-eyebrow inline-block text-sky-600 mb-3">FORECASTER</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">
            See it <span className="bf-grad-text">before</span> the RFP drops.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl">Sources Sought, presolicitation notices, special notices and J&amp;As pulled straight from SAM.gov — so you can shape requirements early instead of chasing them.</p>
        </div>
      </div>

      {/* Type summary chips */}
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="forecaster-type-summary">
          {Object.entries(counts).map(([t, n]) => {
            const meta = typeMeta[t] || { tile: "bg-slate-100 text-slate-700 border-slate-200", label: (t || "OTHER").toUpperCase() };
            return (
              <span key={t} className={`text-[11px] font-mono font-bold tracking-widest px-2.5 py-1 rounded-full border ${meta.tile}`}>{meta.label} · {n}</span>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid="forecaster-filters">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">KEYWORD</div>
            <input value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="e.g. roofing" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="forecaster-keyword" />
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">NAICS</div>
            <input value={f.naics} onChange={(e) => setF({ ...f, naics: e.target.value })} placeholder="238160" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="forecaster-naics" />
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">STATE</div>
            <input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="TX" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="forecaster-state" />
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">POSTED (DAYS BACK)</div>
            <select value={f.days_back} onChange={(e) => setF({ ...f, days_back: Number(e.target.value) })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="forecaster-days">
              {[14, 30, 60, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={load} disabled={loading} className="bf-pill px-6 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="forecaster-search">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />SCANNING</> : <><Search className="w-4 h-4" />FORECAST</>}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {loading && rows.length === 0 && (
          <div className="p-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Scanning SAM.gov…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-12 text-center text-slate-500 text-sm">
            <TrendingUp className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No pre-solicitation notices for these filters. Widen your criteria and try again.
          </div>
        )}
        {rows.map((o) => {
          const meta = typeMeta[o.type] || { tile: "bg-slate-100 text-slate-700 border-slate-200", label: (o.type || "OTHER").toUpperCase() };
          return (
            <Link key={o.notice_id} to={`/opportunities/${o.notice_id}`} className="block p-5 border-b border-slate-100 hover:bg-violet-50/40 transition-colors last:border-b-0" data-testid={`forecaster-row-${o.notice_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-[10px] font-mono font-bold tracking-widest px-2.5 py-0.5 rounded-full border ${meta.tile}`}>{meta.label}</span>
                    <span className="text-[11px] font-mono text-slate-500 truncate">{o.agency || "—"}{o.state ? ` · ${o.state}` : ""}</span>
                    {o.posted_date && <span className="text-[11px] font-mono text-slate-400 inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(o.posted_date).toLocaleDateString()}</span>}
                  </div>
                  <div className="font-semibold text-slate-900">{o.title}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {o.naics && <span className="text-[10px] font-mono font-bold tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">NAICS {o.naics}</span>}
                    {o.response_deadline && <span className="text-[10px] font-mono font-bold tracking-wider text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">Responses due {new Date(o.response_deadline).toLocaleDateString()}</span>}
                  </div>
                </div>
                {o.url && <a href={o.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] font-mono font-bold tracking-widest text-slate-400 hover:text-violet-600 flex items-center gap-1 shrink-0" data-testid={`forecaster-sam-${o.notice_id}`}><ExternalLink className="w-3 h-3" />SAM</a>}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
